import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2, Thermometer, CloudSun, Umbrella, Wind, Calendar } from 'lucide-react';

export const WeatherTab = ({ weatherData, loadingWeather, tripInput }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return dateStr;
  };

  const hasTravelDates = tripInput?.start_date && tripInput?.end_date;
  const forecastTitle = hasTravelDates
    ? `Forecast for ${formatDate(tripInput.start_date)} to ${formatDate(tripInput.end_date)}`
    : '7-Day Forecast';

  return (
    <div className="space-y-6">
      {loadingWeather ? (
        <div className="flex flex-col items-center justify-center p-12 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-ocean" />
          <p className="text-ocean/60 text-sm">Fetching weather forecast... This may take a few seconds.</p>
        </div>
      ) : weatherData ? (
        <>
          {weatherData.current && (
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200/50 shadow-xl">
              <CardHeader><CardTitle className="font-serif text-xl text-ocean flex items-center gap-2"><Thermometer className="w-5 h-5 text-burnt" />Current Weather</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-orange-100/80 rounded-xl border border-orange-200/50">
                    <p className="text-3xl font-bold text-ocean">{weatherData.current.temperature_c}&deg;C</p>
                    <p className="text-sm text-ocean/60">{weatherData.current.temperature_f}&deg;F</p>
                  </div>
                  <div className="text-center p-4 bg-sky-100/80 rounded-xl border border-sky-200/50">
                    <p className="text-lg font-semibold text-ocean">{weatherData.current.condition}</p>
                    <p className="text-sm text-ocean/60">Condition</p>
                  </div>
                  <div className="text-center p-4 bg-blue-100/80 rounded-xl border border-blue-200/50">
                    <p className="text-lg font-semibold text-ocean">{weatherData.current.humidity}</p>
                    <p className="text-sm text-ocean/60">Humidity</p>
                  </div>
                  <div className="text-center p-4 bg-teal-100/80 rounded-xl border border-teal-200/50">
                    <p className="text-lg font-semibold text-ocean">{weatherData.current.wind}</p>
                    <p className="text-sm text-ocean/60">Wind</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {weatherData.forecast && (
            <Card className="bg-gradient-to-br from-sky-50 to-blue-50 border-sky-200/50 shadow-xl">
              <CardHeader><CardTitle className="font-serif text-lg text-ocean flex items-center gap-2"><CloudSun className="w-5 h-5 text-sky-500" />{forecastTitle}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-2">
                  {weatherData.forecast.map((day, i) => (
                    <div key={`forecast-${day.day}-${i}`} className="text-center p-3 bg-white/70 rounded-xl border border-sky-200/40 hover:bg-white/90 transition-colors">
                      <p className="text-xs font-bold text-ocean/70">{day.day}</p>
                      <p className="text-sm font-semibold text-ocean">{day.high_c}&deg;</p>
                      <p className="text-xs text-ocean/50">{day.low_c}&deg;</p>
                      <p className="text-[10px] text-ocean/60 mt-1">{day.condition}</p>
                      <p className="text-[10px] text-blue-500 font-medium">{day.rain_chance}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {weatherData.packing_advice && (
            <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200/50 shadow-xl">
              <CardContent className="p-6">
                <p className="font-semibold text-ocean mb-2 flex items-center gap-2"><Umbrella className="w-4 h-4 text-emerald-500" /> Packing Advice</p>
                <p className="text-charcoal text-sm">{weatherData.packing_advice}</p>
              </CardContent>
            </Card>
          )}
          {weatherData.best_time_to_visit && (
            <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200/50 shadow-xl">
              <CardContent className="p-6">
                <p className="font-semibold text-ocean mb-2 text-lg flex items-center gap-2"><Calendar className="w-5 h-5 text-violet-500" /> Best Time to Visit</p>
                <p className="text-charcoal text-sm">{weatherData.best_time_to_visit}</p>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <div className="text-center p-12 text-ocean/50"><p>Click the Weather tab to load forecast data</p></div>
      )}
    </div>
  );
};
